import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classicStyle from "./HeadSearch.module.scss";
import modernStyle from "./HeadSearchModern.module.scss";
import searchResultsStyle from "./SearchResults.module.scss";
import classNames from "classnames";
import SearchResults from "./SearchResults";
import {
  allAutocompleteThunk,
  allSuggestionsThunk,
  clearSuggestions,
  combinedSearchThunk,
  selectAutoCompleteTextPlus,
  selectSuggestions,
  loadSearchMetaData,
  clearEnhancedSearchAutoComplete,
  selectTopicQuery,
} from "../../../features/enhancedSearch";
import { saveRecentSearches } from "../../../features/enhancedSearch/recentSearchService";
import { useDebouncedCallback } from "use-debounce";
import {
  saveSearchThunk,
  selectIfSearchMatchesSavedSearch,
} from "../../../features/enhancedSearch";
import useAppModernisation from "../../../hooks/useAppModernisation";
import { resetState } from "../../../features/counter/counterSlice";

function shouldWeShowSuggestionText(suggestedText, searchValue) {
  return (
    suggestedText !== "" &&
    searchValue.length > 3 &&
    `${suggestedText}`.startsWith(searchValue) &&
    suggestedText !== searchValue
  );
}

export const HeadSearch = ({
  classicView: menuBarView,
  className,
  push,
  isDesktop,
  isAppMode,
  searchQuery,
}) => {
  const dropdownMobileRef = useRef(null);
  const inputFieldRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const prevSearchValueRef = useRef("");
  const dispatch = useDispatch();
  const suggestionsSelector = useSelector(selectSuggestions);
  const suggestedText = useSelector(selectAutoCompleteTextPlus);
  const previousSearchQuery = useSelector(selectTopicQuery);
  const [showSavedOrRecent, setshowSavedOrRecent] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const savedSearchMatchesCurrentSearch = useSelector(selectIfSearchMatchesSavedSearch);
  const [selectedFacet] = useState();
  const showSuggestedText = shouldWeShowSuggestionText(suggestedText, searchValue);
  const { isWebModernisationEnabled } = useAppModernisation();
  const styleSearch = isWebModernisationEnabled && !isAppMode ? modernStyle : classicStyle;

  const fetchAucompleteAndSuggestion = useDebouncedCallback((searchTerm, isAutoComplete) => {
    const trimmedValue = searchTerm.trim().toLowerCase();
    if (trimmedValue && trimmedValue !== "") {
      dispatch(allSuggestionsThunk({ searchText: trimmedValue }));
      if (!isAutoComplete) {
        dispatch(allAutocompleteThunk({ searchText: trimmedValue }));
      }
      setIsOpen(true);
      setshowSavedOrRecent(false);
      setShowSuggestion(true);
    }
  }, 800);

  const handleInputChange = (e) => {
    setIsOpen(true);
    const newValue = e.target.value;
    setSearchValue(newValue);

    if (newValue === "") {
      setShowSuggestion(false);
      setshowSavedOrRecent(true);
      return;
    }

    if (newValue !== prevSearchValueRef.current) {
      setShowSuggestion(false);
      setshowSavedOrRecent(false);

      if (newValue.length >= 3) {
        fetchAucompleteAndSuggestion(newValue, false);
      }
    }
  };

  const handleFocus = () => {
    if (isAppMode) {
      push("/app/searchLanding");
    }
    setIsOpen(true);
    setshowSavedOrRecent(true);

    if (searchValue === prevSearchValueRef.current && searchValue.trim()) {
      setshowSavedOrRecent(false);
    }
  };

  const handleBlur = () => {
    prevSearchValueRef.current = searchValue;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchValue.length >= 2) {
      fetchAucompleteAndSuggestion.cancel();
      // Capture spell-correction hint BEFORE clearing autocomplete.
      // A suggestion is a correction (not just a prefix completion) when it
      // exists, differs from what the user typed, and does not merely extend it.
      const spellCorrection =
        suggestedText &&
        suggestedText.toLowerCase() !== searchValue.toLowerCase() &&
        suggestedText.toLowerCase().startsWith(searchValue.toLowerCase())
          ? suggestedText
          : null;
      dispatch(clearEnhancedSearchAutoComplete());
      dispatch(clearSuggestions());
      dispatch(
        combinedSearchThunk({
          searchText: searchValue,
          top: previousSearchQuery?.top,
        }),
      );
      //sending home page search term to tag manager
      console.log("sending home page search term to tag manager", searchValue);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "page_view",
        _event: "page_view",
        "C5-EventType": "Searches Regular",
        //  "C5IP": ip|| 'N/A',
        searchTerm: searchValue || "",
      });
      push(
        `/searchAction/?appendedinputbuttons=${encodeURIComponent(searchValue)}${
          spellCorrection ? `&correction=${encodeURIComponent(spellCorrection)}` : ""
        }`,
      );
      inputFieldRef.current.blur();
      setIsOpen(false);
    }
    if ((e.key === "Tab" || e.keyCode === 9) && searchValue.length >= 3) {
      if (suggestedText && suggestedText !== searchValue) {
        // When displayed on the homescreen, the tab moves the cursor to a cancel search button
        // which isn't desired.
        if (!menuBarView) {
          e.preventDefault();
        }
        setSearchValue(suggestedText);
        fetchAucompleteAndSuggestion(suggestedText, true);
        setIsOpen(true);
      }
    } else {
      fetchAucompleteAndSuggestion.cancel();
    }
  };

  const buildSearchUrl = (searchTerm, guidelineTitle) => {
    let baseUrl = `/searchAction/?appendedinputbuttons=${encodeURIComponent(searchTerm)}`;
    if (guidelineTitle) {
      baseUrl += `&guidelinefilter=${encodeURIComponent(guidelineTitle)}`;
    }
    return baseUrl;
  };

  const saveSearchOnClick = () => {
    if (searchValue) {
      const url = buildSearchUrl(searchValue, selectedFacet);
      dispatch(
        saveSearchThunk({
          searchTerm: searchValue,
          searchUrl: url,
          remove: savedSearchMatchesCurrentSearch,
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(loadSearchMetaData());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownMobileRef.current &&
        !dropdownMobileRef.current.contains(event.target) &&
        event.target !== inputFieldRef.current
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(searchQuery);
    if (searchParams.has("appendedinputbuttons")) {
      const initialValue = searchParams.get("appendedinputbuttons");
      setSearchValue(initialValue);
      prevSearchValueRef.current = initialValue;
    }
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchValue("");
    setIsOpen(false);
    setshowSavedOrRecent(false);
    setShowSuggestion(false);
    inputFieldRef.current.blur();
  };

  const suggestionItemClick = (item) => {
    try {
      saveRecentSearches("combinedSearch", {
        searchText: item.suggestion,
        url: item.url,
        title: item.title,
      });
      dispatch(resetState());
      setTimeout(() => {
        push(item.url);
        inputFieldRef.current.blur();
      }, 200);
    } catch (error) {
      console.error(error);
    }
    setIsOpen(false);
  };

  const keypressSearchHandler = (event) => {
    console.log(" this is in the navbar head searchkeypressSearchHandler", event, searchValue);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      _event: "page_view",
      "C5-EventType": "Searches Regular",
      //  "C5IP": ip|| 'N/A',
      searchTerm: searchValue || "",
    });
    if (event.keyCode === 13 && searchValue?.length >= 3) {
      const spellCorrection =
        suggestedText &&
        suggestedText.toLowerCase() !== searchValue.toLowerCase() &&
        suggestedText.toLowerCase().startsWith(searchValue.toLowerCase())
          ? suggestedText
          : null;
      fetchAucompleteAndSuggestion.cancel();
      setIsOpen(false);
      dispatch(
        combinedSearchThunk({
          searchText: searchValue,
          top: previousSearchQuery?.top,
        }),
      );
      push(
        `/searchAction/?appendedinputbuttons=${encodeURIComponent(searchValue)}${
          spellCorrection ? `&correction=${encodeURIComponent(spellCorrection)}` : ""
        }`,
      );
      inputFieldRef.current.blur();
    }
  };

  const goBack = () => {
    if (searchValue) {
      push(-1);
    }
  };

  return (
    <div
      className={classNames({
        [styleSearch["classic-container"]]: menuBarView,
        [styleSearch["container"]]: menuBarView !== true,
        [styleSearch["relative"]]: isDesktop === true,
        [styleSearch["appMode"]]: isAppMode === true,
        "size-16": isAppMode === true,
        "size-14": isAppMode !== true,
        [styleSearch[className]]: styleSearch[className] !== undefined,
      })}
    >
      {isAppMode && (
        <div className={styleSearch["imageContainer"]}>
          <img src="/TherapeuticGuidelines_Original_R_Logo.svg" alt="Therapeutic Guidelines" />
        </div>
      )}
      <div
        className={classNames(`tg-icons-Magnifier ${styleSearch["icon-placement"]}`, {
          [styleSearch["classic-icon"]]: menuBarView,
        })}
      >
        {menuBarView && isWebModernisationEnabled && (
          <button
            type="button"
            className={classNames(styleSearch["leftIcon"], "size-14", {
              [styleSearch.rotated]: searchValue,
            })}
            aria-label="Go Back"
            data-testid="backButton"
            onClick={goBack}
          >
            <i
              className={classNames({
                "tg-icons-Magnifier": !searchValue,
                "tg-icons-LeftArrow": searchValue,
              })}
            ></i>
          </button>
        )}
        {showSuggestedText && (
          <div
            className={classNames(styleSearch["autocompleteWrapper"], {
              [styleSearch["classicSuggestion"]]: menuBarView && !isWebModernisationEnabled,
            })}
          >
            <span className={styleSearch["typedText"]}>{searchValue}</span>
            <span className={classNames(styleSearch["suggestedText"], "medium")}>
              {suggestedText.slice(searchValue.length)}
            </span>
          </div>
        )}

        <input
          type="search"
          name="appendedinputbuttons"
          id="searchBoxEnhanced"
          className={classNames(styleSearch.inputsearch, {
            [styleSearch["classic"]]: menuBarView,
            [styleSearch["inputAppMode"]]: isAppMode === true,
          })}
          placeholder={isAppMode || !isWebModernisationEnabled ? "Search" : ""}
          autoComplete="off"
          onKeyDown={handleKeyDown}
          value={searchValue}
          onChange={handleInputChange}
          ref={inputFieldRef}
          aria-autocomplete="both"
          aria-owns="searchResults"
          data-testid="search-input"
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {menuBarView === true && (
          <button
            type="button"
            className={styleSearch["search-button"]}
            data-testid="magnifier"
            onClick={() => keypressSearchHandler({ keyCode: 13 })}
          >
            <i className="flaticon-magnifying-glass34"></i>
          </button>
        )}

        {menuBarView && searchValue && (
          <button
            type="button"
            className={classNames(styleSearch["saveIcon"], "size-18")}
            aria-label="Save search"
            onClick={saveSearchOnClick}
          >
            <i
              className={classNames({
                "tg-icons-Saved": !savedSearchMatchesCurrentSearch,
                "tg-icons-SavedFilled": savedSearchMatchesCurrentSearch,
              })}
              data-testid={
                savedSearchMatchesCurrentSearch ? "filled-saved-icon" : "empty-saved-icon"
              }
            ></i>
          </button>
        )}
        {menuBarView !== true && searchValue !== "" && (
          <button
            onClick={clearSearch}
            aria-label="Clear Search"
            data-testid="clear-button"
            type="button"
            className={classNames(styleSearch["clear-button"], "size-24")}
          >
            <i className="tg-icons-Xmark"></i>
          </button>
        )}
      </div>
      {isOpen && (
        <SearchResults
          ref={dropdownMobileRef}
          items={suggestionsSelector}
          handleSelect={suggestionItemClick}
          type="search"
          resultStyle={searchResultsStyle}
          showSavedOrRecent={showSavedOrRecent}
          showSuggestion={showSuggestion}
          searchValue={searchValue}
        />
      )}
    </div>
  );
};
